import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import { validateField } from '@/helpers/campaignValidations';
import {
  supervisor, qmbApprover, HELP_AREA, APPROVER_MIN_REQUIRED_LEVELS, QMB_PLANT_APPROVER_CR, CAMPAIGN_MANAGER, ADMIN, CAMPAIGN_MANAGER_JV, ERROR,
  COMMENT_FIELD, stateTabs, ADMIN_CR, statusSubCampaign
} from '@/helpers/constants';
import { formatSummaryUser } from '@/helpers/utils';
import useAuth from '@/hooks/useAuth';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignRecipients } from '@/redux/Campaign/campaign.action';
import { getCampaignInfo, getCampaignRecipients, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { setFeedbackValidations, setSubCampaignFeedback, setSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.action';
import { getFeedbackValidations, getSubCampaignFeedback, getSubCampaignInfo, getSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.reducer';
import { setSettingsInfo } from '@/redux/User/user.action';
import { getSettingsInfo, getUserInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import { Language, SubCampaignFeedback, OriginalValues, SubCampaignInfo, UserInfo, UserSearch, UserSettings, Validations, Recipients, User } from '@/types';
import styles from './Feedback.module.scss';

const RECIPIENTS_MODULE = stateTabs.recipients;
const FEEDBACK_SECTION = stateTabs.feedback;
const APPROVERS_SECTION = 'approvers' as const;
const COMMENT_FOR_QMB_APPROVER_FIELD = 'commentForQmbApprover' as const;
const IMPORTANT_INFORMATION_FIELD = 'importantInformation' as const;
const E3_PLANT_SELECTED_SETTING = 'e3PlantSelected' as const;
const E4_PLANT_SELECTED_SETTING = 'e4PlantSelected' as const;

interface ApproverCommentSectionProps {
  isVANCR: boolean;
  setHelpArea: (help: string) => void;
  subCampaignFeedback: SubCampaignFeedback;
  subCampaignInfo: SubCampaignInfo;
  headerLanguage: Language;
  validations: Validations;
  setValidations: (validations: Validations) => void;
  setFeedback: (feedback: SubCampaignFeedback) => void, // eslint-disable-line 
  setSettings: (settings: UserSettings) => void, // eslint-disable-line 
  settingsInfo: UserSettings;
  setOriginalValues: (originalValues: OriginalValues) => void, // eslint-disable-line 
  originalValues: OriginalValues;
  handleChangeText: (label: string, value: string, section: string | null, isSyncField: boolean, noCampaignField: boolean) => void;
  recipients: Recipients;
  setRecipients: (recipients: Recipients) => void;
  userInfo: Partial<User>;
}

const ApproverCommentSection: React.FC<ApproverCommentSectionProps> = (props) => {
  const { isVANCR, setHelpArea, subCampaignFeedback, subCampaignInfo, headerLanguage, validations, setValidations, setOriginalValues,
    settingsInfo, originalValues, setFeedback, setSettings, handleChangeText, recipients, setRecipients, userInfo } = props;
  const authService = useAuth();
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const checkDisabled = useCheckDisabled(true, true);
  const isEditableRole = authService?.checkRole(CAMPAIGN_MANAGER) || authService?.checkRole(ADMIN) || authService?.checkRole(CAMPAIGN_MANAGER_JV);
  const importantInformationDisabled = checkDisabled || !isEditableRole;
  const isAdmin = userInfo?.roleSelected === ADMIN_CR;

  useEffect(() => {
    if (!subCampaignInfo?.id || isVANCR) return;
    campaignServices.getSubCampaign(subCampaignInfo.id, RECIPIENTS_MODULE)
      .then((response) => {
        if (response?.module) {
          setRecipients(response?.module);
        }
      })
      .catch((error) => console.error('Error loading recipients:', error)); // eslint-disable-line no-console
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveUsersSettings = (newSettings) => {
    const userSettings = { ...settingsInfo, ...newSettings };
    usersServices
      .saveUsersSettings(authService.userCode, userSettings)
      .then(() => {
        usersServices
          .getUsersSettings(authService.userCode)
          .then((settings) => {
            setSettings(settings);
          })
          .catch((err) => {
            setSettings({});
            addToast(err.message, ERROR);
          });
      })
      .catch((error) => addToast(error.message, ERROR));
  };

  const onSetOriginValues = () => {
    const values = {
      ...originalValues,
      hasChange: true,
    };
    setOriginalValues(values);
  };

  const validateUser = (label: string, value: UserInfo) => {
    const users = [qmbApprover];

    if (users.includes(label)) {
      const newState = {
        ...validations,
        [headerLanguage]: {
          [label]: validateField(validations, value.code, { required: true }),
        },
      };
      setValidations(newState);
    }
  };

  const handleChangeUser = (label: string, value: UserInfo) => {
    const newState = {
      ...subCampaignFeedback,
      approvers: {
        ...subCampaignFeedback.approvers,
        [label]: value,
      },
    };
    validateUser(label, value);
    onSetOriginValues();
    // @ts-expect-error:next-line
    setFeedback(newState);
    const userLabel = label === qmbApprover ? E3_PLANT_SELECTED_SETTING : E4_PLANT_SELECTED_SETTING;
    saveUsersSettings({ ...settingsInfo, [userLabel]: value.code });
  };

  const updateUser = (label: string, userSelected: UserSearch | null) => {
    if (label === qmbApprover && recipients?.causerApprover?.code && userSelected?.userCode === recipients.causerApprover.code && !isVANCR) {
      addToast(t('errors.approverCauserSame'), ERROR);
    } else {
      const newPerson = {
        code: userSelected ? userSelected?.userCode : '',
        department: userSelected ? userSelected.department : '',
        summary: userSelected ? formatSummaryUser(userSelected) : '',
      };
      handleChangeUser(label, newPerson);
    }
  };

  const editableStatuses: string[] = [
    statusSubCampaign.IN_PLANT_COORDINATION,
    statusSubCampaign.IN_PLANT_APPROVAL,
    statusSubCampaign.IN_E3_APPROVAL,
  ];
  const isEditableStatus = editableStatuses.includes(subCampaignInfo?.status);

  return !isVANCR
    ? (
      <AccordionPanel
        title={t('feedback.approverTitle')}
        handleClick={() => setHelpArea(HELP_AREA.APPROVER)}
      >
        <div className={styles.SectionRow} data-testid="feedback-approvers-section">
          <div className={styles.FourColumns}>
            <InputUser
              disabled={checkDisabled}
              id="input-responsible-requester"
              fieldPath={['feedback', 'approvers', supervisor]}
              userFieldPath={['feedback', 'approvers', supervisor]}
              label={t('recipients.supervisor')}
              updateUserSelected={(userSelected: UserSearch | null) => updateUser(supervisor, userSelected)}
              clear={() => { updateUser(supervisor, null); }}
              value={subCampaignFeedback.approvers?.supervisor?.summary}
              code={subCampaignFeedback.approvers?.supervisor?.code}
              title={t('recipients.supervisor')}
              level={undefined}
              plant={subCampaignInfo?.plant}
              authorizedPlants
            />
          </div>
          <div className={styles.FourColumns}>
            <InputUser
              disabled={!(isAdmin && isEditableStatus) && checkDisabled}
              isMandatory
              id="input-responsible-requester"
              fieldPath={['feedback', 'approvers', qmbApprover]}
              userFieldPath={['feedback', 'approvers', qmbApprover]}
              label={t('recipients.qmbApprover')}
              updateUserSelected={(userSelected: UserSearch | null) => updateUser(qmbApprover, userSelected)}
              clear={() => { updateUser(qmbApprover, null); }}
              value={subCampaignFeedback.approvers?.qmbApprover?.summary}
              code={subCampaignFeedback.approvers?.qmbApprover?.code}
              title={t('recipients.qmbApprover')}
              level={APPROVER_MIN_REQUIRED_LEVELS[QMB_PLANT_APPROVER_CR]}
              isFixedLevel={false}
              plant={subCampaignInfo?.plant}
              authorizedPlants
            />
          </div>
          <div className={styles.TwoColumns}>
            <InputText
              disabled={checkDisabled}
              multiline
              rows={4}
              maxRows={5}
              maxLength={512}
              id="feedback-comment"
              fieldPath={[FEEDBACK_SECTION, APPROVERS_SECTION, COMMENT_FOR_QMB_APPROVER_FIELD]}
              noTranslationField
              label={t('feedback.commentForQmbApprover')}
              value={subCampaignFeedback.approvers?.commentForQmbApprover || ''}
              handleChange={(ev) => handleChangeText(COMMENT_FOR_QMB_APPROVER_FIELD, ev.target.value, APPROVERS_SECTION, false, true)}
              helperText={[
                ...(!checkDisabled && subCampaignFeedback.approvers?.commentForQmbApprover?.length === 512
                  ? [t('validations.maxLength', { maxLength: 512 })]
                  : []),
              ]}
            />
          </div>
          <div className={styles.TwoColumns} data-testid="feedback-important-information-section">
            <InputText
              disabled={importantInformationDisabled}
              multiline
              rows={4}
              maxRows={5}
              maxLength={512}
              id="feedback-important-information"
              fieldPath={[FEEDBACK_SECTION, IMPORTANT_INFORMATION_FIELD]}
              userFieldPath={[FEEDBACK_SECTION, IMPORTANT_INFORMATION_FIELD]}
              noTranslationField
              label={t('feedback.importantInformation')}
              value={subCampaignFeedback.approvers?.importantInformation || ''}
              handleChange={(ev) => handleChangeText(IMPORTANT_INFORMATION_FIELD, ev.target.value, APPROVERS_SECTION, false, true)}
              helperText={[
                ...(!importantInformationDisabled && subCampaignFeedback.approvers?.importantInformation?.length === 512
                  ? [t('validations.maxLength', { maxLength: 512 })]
                  : []),
              ]}
            />
          </div>
        </div>
      </AccordionPanel>
    )
    : (
      <AccordionPanel
        title={t('feedback.comments')}
        handleClick={() => setHelpArea(HELP_AREA.APPROVER_COMMENT_VANS)}
      >
        <div className={styles.TwoColumns} data-testid="feedback-comments-section">
          <InputText
            disabled={checkDisabled}
            multiline
            rows={4}
            maxRows={5}
            maxLength={512}
            id="feedback-van-comment"
            fieldPath={[FEEDBACK_SECTION, COMMENT_FIELD]}
            userFieldPath={[FEEDBACK_SECTION, COMMENT_FIELD]}
            noTranslationField
            label={t('feedback.comment')}
            value={subCampaignFeedback.comment || ''}
            handleChange={(ev) => handleChangeText(COMMENT_FIELD, ev.target.value, null, false, true)}
            helperText={[
              ...(!checkDisabled && subCampaignFeedback.comment?.length === 512
                ? [t('validations.maxLength', { maxLength: 512 })]
                : []),
            ]}
          />
        </div>
        <div className={styles.TwoColumns} data-testid="feedback-important-information-section">
          <InputText
            disabled={importantInformationDisabled}
            multiline
            rows={4}
            maxRows={5}
            maxLength={512}
            id="feedback-important-information"
            fieldPath={['feedback', 'importantInformation']}
            userFieldPath={['feedback', 'importantInformation']}
            noTranslationField
            label={t('feedback.importantInformation')}
            value={subCampaignFeedback.approvers?.importantInformation || ''}
            handleChange={(ev) => handleChangeText('importantInformation', ev.target.value, 'approvers', false, true)}
            helperText={[
              ...(!importantInformationDisabled && subCampaignFeedback.approvers?.importantInformation?.length === 512
                ? [t('validations.maxLength', { maxLength: 512 })]
                : []),
            ]}
          />
        </div>
      </AccordionPanel>
    );
};

const mapStateToProps = (state) => ({
  subCampaignFeedback: getSubCampaignFeedback(state),
  subCampaignInfo: getSubCampaignInfo(state),
  validations: getFeedbackValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  isVANCR: getIsVANCR(state),
  originalValues: getSubCampaignOriginalValues(state),
  settingsInfo: getSettingsInfo(state),
  recipients: getCampaignRecipients(state),
  userInfo: getUserInfo(state),
});

const actionCreators = {
  setFeedback: setSubCampaignFeedback,
  setHelpArea: setAreaHelp,
  setValidations: setFeedbackValidations,
  setOriginalValues: setSubCampaignOriginalValues,
  setSettings: setSettingsInfo,
  setRecipients: setCampaignRecipients,
};

export default (connect(mapStateToProps, actionCreators)(ApproverCommentSection));
